import { getFileUrl } from "@/lib/api";
import { Activity, Building2, Calendar, ClipboardList, Eye, Filter, Package, Truck, MoreHorizontal, Paperclip, ToggleLeft, ToggleRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ActionMenu from "../../../components/shared/ActionMenu";
import CurrencyDisplay from "../../../components/shared/CurrencyDisplay";
import DataTable from "../../../components/shared/DataTable";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import { Button } from "../../../components/ui/button";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { formatDate } from "../../../utils/dateUtils";

const MaterialTable = ({
  materials,
  permissions,
  onEdit,
  onDelete,
  onViewPhoto,
  filters = {},
  onFilterChange,
  vendorId,
  onVendorChange,
  showDeleted,
  setShowDeleted,
  setPage
}) => {
  const navigate = useNavigate();

  const materialColumns = [
    {
      header: "Material",
      accessor: "name",
      isPrimary: true,
      render: (val, material) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-200 dark:border-yale-blue-800 bg-slate-100 dark:bg-yale-blue-800 flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-slate-400" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100 whitespace-normal break-words">
                {material.name}
              </span>
              {material.deleted && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                  Deleted
                </span>
              )}
            </div>
            {material.challanSlipNumber && (
              <span className="text-[10px] text-slate-500 font-mono">#{material.challanSlipNumber}</span>
            )}
          </div>
        </div>
      )
    },
    {
      header: "Project",
      accessor: "projectId",
      render: (val, material) => {
        const project = material.project;
        return (
          <div
            className="text-sm text-yale-blue-600 dark:text-yale-blue-400 hover:text-yale-blue-700 dark:hover:text-yale-blue-300 cursor-pointer font-medium flex items-center gap-1.5 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${material.project?.id || material.projectId}`);
            }}
          >
            <Building2 className="w-3.5 h-3.5 shrink-0" />
            <span>{project?.name || "N/A"}</span>
          </div>
        );
      }
    },
    {
      header: "Quantity",
      accessor: "quantity",
      render: (val, material) => (
        <div className="flex flex-col">
          <span className="text-sm font-bold text-yale-blue-700 dark:text-yale-blue-400">
            {val} {material.unit}
          </span>
        </div>
      )
    },
    {
      header: "Cost",
      accessor: "costPerUnit",
      render: (val, material) => (
        <div className="flex flex-col">
          <div className="flex items-center text-sm font-medium text-green-600">
            <CurrencyDisplay amount={typeof val === "string" ? val : val * material.quantity} />
          </div>
          <span className="text-[10px] text-slate-500">
            {typeof val === "number" ? `₹${val.toFixed(2)} / ${material.unit}` : val}
          </span>
        </div>
      )
    },
    {
      header: "Vendor",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${vendorId ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Vendor</p>
              <LazySearchSelect
                value={vendorId}
                onChange={onVendorChange}
                placeholder="Select Vendor"
                searchPlaceholder="Search vendors..."
                apiUrl="/vendors"
                labelKey="vendorName"
                pageSize={20}
                isClearable={true}
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "supplier",
      render: (val, material) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Truck className="w-3.5 h-3.5 text-slate-400" />
          <span className="truncate max-w-[150px]">
            {val?.vendorName || material.vendorName || material.supplierName || "N/A"}
          </span>
        </div>
      )
    },

    {
      header: "Delivery Date",
      accessor: "deliveredDate",
      sortable: true,
      sortKey: "deliveredDate",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Calendar className="w-3.5 h-3.5 text-yale-blue-500" />
          {val ? formatDate(val) : "N/A"}
        </div>
      )
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setPage(0);
                }}
                id="show-deleted-materials-table"
              />
              <Label htmlFor="show-deleted-materials-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, material) => {
        const hasPhotos = material.photos && material.photos.length > 0;
        return (
          <div className="flex items-center justify-center gap-1">
            {hasPhotos && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 gap-1 text-yale-blue-600 dark:text-yale-blue-400 hover:text-yale-blue-700 dark:hover:text-yale-blue-300 rounded-full hover:bg-yale-blue-50 dark:hover:bg-yale-blue-900/20 flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  const urls = material.photos.map((photo) => getFileUrl(photo));
                  onViewPhoto(urls);
                }}
                title={`View ${material.photos.length} attachment(s)`}
              >
                <Paperclip className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">{material.photos.length}</span>
              </Button>
            )}
            <ActionMenu
              canEdit={permissions?.canEdit && !material.deleted}
              canDelete={permissions?.canDelete && !material.deleted}
              onEdit={() => onEdit(material.id)}
              onDelete={() => onDelete(material.id)}
              menuWidth="w-56"
              quickLinksLabel="Quick Actions"
              quickLinks={
                <>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate(`/tasks?projectId=${material.project?.id || material.projectId}`)}
                  >
                    <ClipboardList className="w-4 h-4 mr-2" /> Tasks
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate(`/activities?projectId=${material.project?.id || material.projectId}`)}
                  >
                    <Activity className="w-4 h-4 mr-2" /> Activities
                  </DropdownMenuItem>
                </>
              }
            />
          </div>
        );
      }
    }
  ];

  return (
    <DataTable
      columns={materialColumns}
      data={materials}
      emptyStateTitle="No materials found"
      emptyStateDescription="Click 'Add Material' to record your first delivery."
      emptyStateIcon={Package}
      sortBy={filters.sortBy}
      direction={filters.direction}
      onSort={(key, dir) => {
        onFilterChange("sortBy", key);
        onFilterChange("direction", dir);
      }}
    />
  );
};

export default MaterialTable;
